import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Users, BookOpen, TrendingUp, Crown, Calendar, 
    Activity, Loader2, ShieldCheck, RefreshCw, Radio,
    Clock, UserPlus, FileText, Zap, Eye
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area
} from 'recharts';

const COLORS = ['#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

// Animated counter component
const AnimatedCounter = ({ value, duration = 500 }) => {
    const [displayValue, setDisplayValue] = useState(0);
    
    useEffect(() => {
        let startTime;
        let animationFrame;
        const startValue = displayValue;
        
        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            
            setDisplayValue(Math.floor(startValue + (value - startValue) * progress));
            
            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };
        
        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [value, duration]);
    
    return <span>{displayValue.toLocaleString()}</span>;
};

// Live indicator component
const LiveIndicator = ({ isLive }) => (
    <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        <span className="text-xs text-muted-foreground">{isLive ? 'Live' : 'Paused'}</span>
    </div>
);

// Activity item component
const ActivityItem = ({ type, data, time }) => {
    const icons = {
        signup: <UserPlus className="w-4 h-4 text-green-500" />,
        entry: <FileText className="w-4 h-4 text-blue-500" />
    };
    
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };
    
    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
        >
            <div className="w-8 h-8 rounded-full bg-background flex items-center justify-center">
                {icons[type]}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                    {type === 'signup' ? `${data.name} signed up` : `${data.user_name} logged mood ${data.emoji || '📝'}`}
                </p>
                <p className="text-xs text-muted-foreground">
                    {type === 'entry' && data.mood_rating && `Rating: ${data.mood_rating}/10 • `}
                    {formatTime(time)}
                </p>
            </div>
        </motion.div>
    );
};

const AdminPage = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState(null);
    const [liveMetrics, setLiveMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLive, setIsLive] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(30); // seconds

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/admin/stats');
            setStats(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch admin stats:', err);
            if (err.response?.status === 403) {
                setError('Access denied. Admin privileges required.');
                setIsLive(false);
            }
        }
    }, [api]);

    const fetchActivity = useCallback(async () => {
        try {
            const response = await api.get('/admin/activity');
            setActivity(response.data);
        } catch (err) {
            console.error('Failed to fetch activity:', err);
        }
    }, [api]);

    const fetchLiveMetrics = useCallback(async () => {
        try {
            const response = await api.get('/admin/metrics/live');
            setLiveMetrics(response.data);
            setLastUpdate(new Date());
        } catch (err) {
            console.error('Failed to fetch live metrics:', err);
        }
    }, [api]);

    const fetchAllData = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchStats(), fetchActivity(), fetchLiveMetrics()]);
        setLoading(false);
    }, [fetchStats, fetchActivity, fetchLiveMetrics]);

    // Initial load
    useEffect(() => {
        fetchAllData();
    }, []);

    // Real-time updates
    useEffect(() => {
        if (!isLive) return;
        
        // Update live metrics every 10 seconds
        const liveInterval = setInterval(fetchLiveMetrics, 10000);
        
        // Update full stats and activity based on refresh interval
        const fullInterval = setInterval(() => {
            fetchStats();
            fetchActivity();
        }, refreshInterval * 1000);
        
        return () => {
            clearInterval(liveInterval);
            clearInterval(fullInterval);
        };
    }, [isLive, refreshInterval, fetchLiveMetrics, fetchStats, fetchActivity]);

    if (loading && !stats) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                    <ShieldCheck className="w-16 h-16 text-muted-foreground" />
                    <h2 className="text-xl font-semibold text-foreground">{error}</h2>
                    <Button onClick={() => navigate('/dashboard')} className="rounded-full">
                        Go to Dashboard
                    </Button>
                </div>
            </DashboardLayout>
        );
    }

    // Prepare chart data
    const subscriptionData = Object.entries(stats?.users_by_subscription || {}).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
    }));

    const genderData = Object.entries(stats?.users_by_gender || {}).map(([name, value]) => ({
        name: name === 'M' ? 'Male' : name === 'F' ? 'Female' : name,
        value
    }));

    const entryTypeData = Object.entries(stats?.entries_by_type || {}).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
    }));

    // Combine activity feed
    const activityFeed = [
        ...(activity?.recent_signups || []).map(s => ({ type: 'signup', data: s, time: s.created_at })),
        ...(activity?.recent_entries || []).map(e => ({ type: 'entry', data: e, time: e.created_at }))
    ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 10);

    return (
        <DashboardLayout>
            <div data-testid="admin-page" className="space-y-6">
                {/* Header with Live Controls */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-semibold text-foreground flex items-center gap-3">
                            <ShieldCheck className="w-8 h-8 text-primary" />
                            Admin Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1 flex items-center gap-2">
                            Real-time platform analytics
                            <LiveIndicator isLive={isLive} />
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={refreshInterval}
                            onChange={(e) => setRefreshInterval(Number(e.target.value))}
                            className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm"
                            data-testid="refresh-interval-select"
                        >
                            <option value={15}>15s refresh</option>
                            <option value={30}>30s refresh</option>
                            <option value={60}>1m refresh</option>
                            <option value={300}>5m refresh</option>
                        </select>
                        <Button
                            onClick={() => setIsLive(!isLive)}
                            variant={isLive ? "default" : "outline"}
                            className="rounded-full"
                            data-testid="toggle-live-btn"
                        >
                            <Radio className={`w-4 h-4 mr-2 ${isLive ? 'animate-pulse' : ''}`} />
                            {isLive ? 'Live' : 'Paused'}
                        </Button>
                        <Button onClick={fetchAllData} variant="outline" className="rounded-full" data-testid="refresh-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Live Metrics Bar */}
                <Card className="glass-card bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-green-500" />
                                    <span className="text-sm text-muted-foreground">Active Now:</span>
                                    <span className="text-lg font-bold text-green-500">
                                        <AnimatedCounter value={liveMetrics?.active_now || 0} />
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <UserPlus className="w-5 h-5 text-blue-500" />
                                    <span className="text-sm text-muted-foreground">New Users (1h):</span>
                                    <span className="text-lg font-bold text-blue-500">
                                        <AnimatedCounter value={liveMetrics?.new_users_1h || 0} />
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-purple-500" />
                                    <span className="text-sm text-muted-foreground">New Entries (1h):</span>
                                    <span className="text-lg font-bold text-purple-500">
                                        <AnimatedCounter value={liveMetrics?.new_entries_1h || 0} />
                                    </span>
                                </div>
                            </div>
                            {lastUpdate && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock className="w-3 h-3" />
                                    Updated: {lastUpdate.toLocaleTimeString()}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <Card className="glass-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Users</p>
                                        <p className="text-3xl font-bold text-foreground">
                                            <AnimatedCounter value={liveMetrics?.total_users || stats?.total_users || 0} />
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <Card className="glass-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                                        <Activity className="w-6 h-6 text-green-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Active (7 days)</p>
                                        <p className="text-3xl font-bold text-foreground">
                                            <AnimatedCounter value={stats?.active_users_7_days || 0} />
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card className="glass-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                                        <BookOpen className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Entries</p>
                                        <p className="text-3xl font-bold text-foreground">
                                            <AnimatedCounter value={liveMetrics?.total_entries || stats?.total_journal_entries || 0} />
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <Card className="glass-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-amber-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Avg Mood</p>
                                        <p className="text-3xl font-bold text-foreground">{stats?.average_mood_rating || 0}/10</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                {/* 24h Stats & Activity Feed */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 24h Overview */}
                    <Card className="glass-card lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl flex items-center gap-2">
                                <Zap className="w-5 h-5 text-amber-500" />
                                Last 24 Hours
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="p-4 rounded-xl bg-green-500/10 text-center">
                                    <p className="text-3xl font-bold text-green-500">
                                        <AnimatedCounter value={activity?.signups_24h || 0} />
                                    </p>
                                    <p className="text-sm text-muted-foreground">New Signups</p>
                                </div>
                                <div className="p-4 rounded-xl bg-blue-500/10 text-center">
                                    <p className="text-3xl font-bold text-blue-500">
                                        <AnimatedCounter value={activity?.entries_24h || 0} />
                                    </p>
                                    <p className="text-sm text-muted-foreground">Journal Entries</p>
                                </div>
                            </div>
                            
                            {/* Hourly Activity Chart */}
                            {activity?.hourly_activity?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <AreaChart data={activity.hourly_activity}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis 
                                            dataKey="hour" 
                                            tick={{ fontSize: 10 }}
                                            tickFormatter={(value) => value.slice(11, 13) + 'h'}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'hsl(var(--background))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="count" 
                                            stroke="#7c3aed" 
                                            fill="#7c3aed"
                                            fillOpacity={0.2}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                                    No hourly data available
                                </div>
                            )}
                            
                            {/* Top Moods Today */}
                            {activity?.top_moods_today?.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-border">
                                    <p className="text-sm font-medium mb-2">Top Moods Today</p>
                                    <div className="flex gap-3">
                                        {activity.top_moods_today.map((mood, i) => (
                                            <div key={i} className="flex items-center gap-1 px-3 py-1 rounded-full bg-muted/50">
                                                <span className="text-lg">{mood.emoji}</span>
                                                <span className="text-sm text-muted-foreground">{mood.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Live Activity Feed */}
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Radio className="w-5 h-5 text-green-500" />
                                    Live Activity
                                </span>
                                <LiveIndicator isLive={isLive} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                <AnimatePresence>
                                    {activityFeed.length > 0 ? (
                                        activityFeed.map((item, index) => (
                                            <ActivityItem
                                                key={`${item.type}-${item.time}-${index}`}
                                                type={item.type}
                                                data={item.data}
                                                time={item.time}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                            <p>No recent activity</p>
                                            <p className="text-xs">Activity from the last hour will appear here</p>
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Signups Chart */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Signups (Last 30 Days)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.signups_last_30_days?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={stats.signups_last_30_days}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) => value.slice(5)}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'hsl(var(--background))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="count" 
                                        stroke="#7c3aed" 
                                        strokeWidth={2}
                                        dot={{ fill: '#7c3aed' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                                No signup data available yet
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Distribution Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Subscription Tiers */}
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <Crown className="w-5 h-5" />
                                Subscriptions
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {subscriptionData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={subscriptionData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={70}
                                            paddingAngle={5}
                                            dataKey="value"
                                            label={({ name, value }) => `${name}: ${value}`}
                                        >
                                            {subscriptionData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                                    No data
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Gender Distribution */}
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Gender
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {genderData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={genderData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={70}
                                            paddingAngle={5}
                                            dataKey="value"
                                            label={({ name, value }) => `${name}: ${value}`}
                                        >
                                            {genderData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                                    No data
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Entry Types */}
                    <Card className="glass-card">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <BookOpen className="w-5 h-5" />
                                Entry Types
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {entryTypeData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={entryTypeData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'hsl(var(--background))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Bar dataKey="value" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                                    No data
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Generated timestamp */}
                <p className="text-center text-sm text-muted-foreground">
                    Full refresh: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString() : 'N/A'}
                </p>
            </div>
        </DashboardLayout>
    );
};

export default AdminPage;
